import { createSelector } from '@reduxjs/toolkit'
import { selectBusinessTravellerApprovalPolicyForUser } from './selectBusinessTravellerApprovalPolicyForUser'
import { selectBusinessTravellerCurrentApprovalRequest } from './selectBusinessTravellerCurrentApprovalRequest'
import { selectBusinessTravellerCarHireBudgetTotal } from './selectBusinessTravellerCarHireBudget'
import { getCarHireItems, getCarHireItemsView } from 'checkout/selectors/view/carHire'
import { getCarHireTotals } from 'checkout/selectors/payment/carHire'
import {
  getCarHireApprovalInvalidReason,
  isCarHireOutOfBudget,
} from 'businessTraveller/utils/carHireApprovalValidation'

/**
 * Selector to determine if we should show the inline car hire trip request form
 * instead of the normal payment form.
 *
 * Rules by approval policy scope:
 * 1. ALWAYS: Show form for all cars except approved cars within (approved budget + buffer)
 * 2. NEVER: Never show the form
 * 3. OUTOFPOLICYONLY: Under company budget → allow (no form). Over budget → require approval (show form).
 *    With invalid approval (wrong car, over approval, etc.) → show form (re-request needed).
 *
 * Returns true while the approval policy is loading to prevent bypass using refreshing the page.
 * Also returns true while budget data is still being fetched for OUTOFPOLICYONLY scope.
 */
export const selectShouldShowInlineCarHireTripRequestForm = createSelector(
  selectBusinessTravellerApprovalPolicyForUser,
  selectBusinessTravellerCarHireBudgetTotal,
  getCarHireItems,
  (state: App.State) => getCarHireTotals(state),
  selectBusinessTravellerCurrentApprovalRequest,
  (state: App.State) => state.businessTraveller.businessApprovalPolicy.status,
  (state: App.State) => state.businessTraveller.businessCarHireBudgets?.car_hire?.status,
  (state: App.State) => getCarHireItemsView(state).data,
  (
    approvalPolicy,
    carHireBudgetTotal,
    carHireItems,
    carHireTotals,
    currentApprovalRequest,
    approvalPolicyStatus,
    carHireBudgetStatus,
    carHireViews,
  ): boolean => {
    const carHireTotal = carHireTotals.hasRequiredData ? carHireTotals.data.price : 0
    const invalidReason =
      currentApprovalRequest?.status === 'APPROVED'
        ? getCarHireApprovalInvalidReason({
            approvalRequest: currentApprovalRequest,
            approvalPolicy,
            carHireViews,
            carHireTotal,
          })
        : undefined

    if (carHireItems.length === 0) {
      return false
    }

    // NEVER: never show the form
    if (approvalPolicy?.scope === 'NEVER') {
      return false
    }

    // Approved car valid (same car, within approved budget + buffer): don't show form for any scope
    if (currentApprovalRequest?.status === 'APPROVED') {
      if (!invalidReason) {
        return false
      }
      // Invalid approval (wrong car, over budget, etc.): must re-request → show form
      if (approvalPolicy?.scope === 'ALWAYS' || approvalPolicy?.scope === 'OUTOFPOLICYONLY') {
        return true
      }
    }

    if (approvalPolicyStatus === 'pending') {
      return true
    }

    if (!approvalPolicy) {
      return false
    }

    // ALWAYS: show form for all cars (no valid approval)
    if (approvalPolicy.scope === 'ALWAYS') {
      return true
    }

    // OUTOFPOLICYONLY: allow under budget, require approval over budget
    if (approvalPolicy.scope === 'OUTOFPOLICYONLY') {
      if (!carHireTotals.hasRequiredData) {
        return true
      }
      if (carHireBudgetStatus !== 'success') {
        return true
      }
      return isCarHireOutOfBudget(carHireTotals.data.price, carHireBudgetTotal)
    }

    return false
  },
)

/**
 * Answers: "If there were no approval in cart, would we show the inline trip request form?"
 * Used by BusinessTravellerCarHireCheckoutExistingApproval to decide whether to clear
 * invalid approval and show the inline form instead of a modal.
 */
export const selectWouldShowInlineTripRequestFormForCarHireIfNoApproval = createSelector(
  selectBusinessTravellerApprovalPolicyForUser,
  selectBusinessTravellerCarHireBudgetTotal,
  getCarHireItems,
  (state: App.State) => getCarHireTotals(state),
  (state: App.State) => state.businessTraveller.businessApprovalPolicy.status,
  (state: App.State) => state.businessTraveller.businessCarHireBudgets?.car_hire?.status,
  (
    approvalPolicy,
    carHireBudgetTotal,
    carHireItems,
    carHireTotals,
    approvalPolicyStatus,
    carHireBudgetStatus,
  ): boolean => {
    if (carHireItems.length === 0) return false
    if (approvalPolicyStatus === 'pending') return true
    if (!approvalPolicy) return false
    if (approvalPolicy.scope === 'NEVER') return false
    if (approvalPolicy.scope === 'ALWAYS') return true
    if (approvalPolicy.scope === 'OUTOFPOLICYONLY') {
      if (!carHireTotals.hasRequiredData) return true
      if (carHireBudgetStatus !== 'success') return true
      const carHireTotal = carHireTotals.data.price
      return isCarHireOutOfBudget(carHireTotal, carHireBudgetTotal)
    }
    return false
  },
)
